'use client';

// Spec 113 — the shared "Market details" step (step 2) for BOTH creation
// methods. Single-form editable view holding cover image + title + ticker +
// description + category + location + outcomes on ONE screen.
//   - Manual path: `aiStreamStatus === 'idle'`, so `pending()` is always
//     false and every field renders immediately (seeded title, blank rest).
//   - AI path: streamed data fills the same fields as each SSE event arrives;
//     a field shows a `<Skeleton>` only while streaming AND still empty.
//
// Visual structure mirrors the legacy manual per-step layouts so the user
// recognises the controls — only the page count differs:
//   - Cover image (left)  |  Title + Description (right)
//   - Market ticker (full row, $ prefix)
//   - Category | Location (2-col grid)
//   - Outcomes — one `<OutcomeInput>` per row.
//
// Image affordances follow the manual-flow pattern: tap the populated
// thumbnail to re-open `<MarketImagePickerModal>` and replace the image.
// A small X overlay clears the slot. There are no "Retry image" buttons —
// retrying is just opening the picker again.
//
// Each field renders a `<Skeleton>` placeholder while the AI is streaming
// AND the corresponding store field is still empty; the moment a streamed
// value arrives, the editable input replaces the skeleton. After streaming
// completes, every field is editable regardless of whether it's empty.
import { c as _c } from "react/compiler-runtime";
import { detectPredictionStyle } from '@morfi/backend-sdk-v2/client';
import { utf8ByteLength } from '@morfi/contract-sdk';
import { domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import type * as React from 'react';
import { useState } from 'react';
import type { MarketImageDraft } from '@/components/images/market-picker';
import { MarketImagePickerModal } from '@/components/images/market-picker-modal';
import { AiStreamStatusChip } from '@/components/launchpad/ai/ai-stream-status-chip';
import { SkeletonField } from '@/components/launchpad/ai/skeleton-field';
import { StreamedImageSlot } from '@/components/launchpad/ai/streamed-image-slot';
import { OutcomeInput } from '@/components/launchpad/outcome-input';
import { SimilarMarketsNudge } from '@/components/launchpad/similar-markets-nudge';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Skeleton } from '@/components/ui/app-skeleton';
import { Icon } from '@/components/ui/icon';
import { Alert01IconSolid, PlusSignIcon } from '@/components/ui/icon/glyphs';
import { InputField } from '@/components/ui/input-field';
import { Label } from '@/components/ui/label';
import { SelectField } from '@/components/ui/select-field';
import { TextareaField } from '@/components/ui/textarea-field';
import { PROPOSAL_FIELDS } from '@/config/proposal-fields';
import { PROPOSAL_CATEGORIES, PROPOSAL_LOCATIONS } from '@/config/proposal-options';
import { useOutcomesLimits } from '@/hooks/launchpad/use-outcomes-limits';
import { useNsfwStatusByUrl } from '@/hooks/use-nsfw-status-by-url';
import { useUppercaseTickerInput } from '@/lib/launchpad/uppercase-ticker-input';
import { MOTION_ENTRANCE } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';
import { getDetailsStepErrors, type StepErrors, selectStepAttempted, useProposalFormStore } from '@/stores/proposal-form';

/** Spec 113 — the consolidated details step is step 2 of the unified flow,
 *  so its inline errors gate on `stepsAttempted.has(2)` (FR-007). */
const DETAILS_STEP = 2;
const FADE_INITIAL = {
  opacity: 0,
  y: 4
} as const;
const FADE_ANIMATE = {
  opacity: 1,
  y: 0
} as const;
type AiOutcome = ReturnType<typeof useProposalFormStore.getState>['outcomes'][number];
type MotionInitial = false | {
  opacity: number;
  y: number;
};
type MotionTransition = {
  duration: number;
} | typeof MOTION_ENTRANCE;
interface SelectOption {
  value: string;
  label: string;
}
const CATEGORY_OPTIONS: SelectOption[] = PROPOSAL_CATEGORIES.map((c): SelectOption => ({
  value: c.name,
  label: `${c.emoji} ${c.name}`
}));
const LOCATION_OPTIONS: SelectOption[] = PROPOSAL_LOCATIONS.map((l): SelectOption => ({
  value: l.name,
  label: `${l.emoji} ${l.name}`
}));
interface ProposalDetailsFormProps {
  locationEnabled: boolean;
  lockedCategory?: string | undefined;
}
export function ProposalDetailsForm(t0) {
  const $ = _c(87);
  const {
    locationEnabled,
    lockedCategory
  } = t0;
  const t = useTranslations("aiProposal.editForm");
  const tForm = useTranslations("launchpad.form");
  const tErrors = useTranslations("launchpad.errors");
  const reduce = useReducedMotion();
  const aiStreamStatus = useProposalFormStore(_temp);
  const isStreaming = aiStreamStatus === "streaming";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = selectStepAttempted(DETAILS_STEP);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const attempted = useProposalFormStore(t1);
  const fullState = useProposalFormStore();
  const stateWithLockedCategory = lockedCategory === undefined ? fullState : {
    ...fullState,
    category: lockedCategory
  };
  const errors = getDetailsStepErrors(stateWithLockedCategory, {
    requireLocation: locationEnabled
  });
  const errText = function errText(field) {
    if (!attempted) {
      return;
    }
    const entry = errors[field];
    if (entry === undefined) {
      return;
    }
    return tErrors(entry.key as Parameters<typeof tErrors>[0], entry.params);
  };
  const title = useProposalFormStore(_temp2);
  const description = useProposalFormStore(_temp3);
  const storedCategory = useProposalFormStore(_temp4);
  const category = lockedCategory ?? storedCategory;
  const location = useProposalFormStore(_temp5);
  const marketTicker = useProposalFormStore(_temp6);
  const coverImageUrl = useProposalFormStore(_temp7);
  const coverImagePreview = useProposalFormStore(_temp8);
  const coverImageDraft = useProposalFormStore(_temp9);
  const coverImageUploading = useProposalFormStore(_temp10);
  const outcomes = useProposalFormStore(_temp11);
  const coverPinFailed = coverImagePreview !== undefined && coverImagePreview.length > 0 && coverImageDraft === null && coverImageUrl.length === 0 && !coverImageUploading;
  const updateField = useProposalFormStore(_temp12);
  let t2;
  if ($[1] !== updateField) {
    t2 = value => {
      updateField("marketTicker", value);
    };
    $[1] = updateField;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const tickerInput = useUppercaseTickerInput(t2);
  const updateOutcomeLabel = useProposalFormStore(_temp13);
  const updateOutcomeTicker = useProposalFormStore(_temp14);
  const addOutcome = useProposalFormStore(_temp15);
  const removeOutcome = useProposalFormStore(_temp16);
  const setOutcomeImageDraft = useProposalFormStore(_temp17);
  const setOutcomeImage = useProposalFormStore(_temp18);
  const setCoverImageDraft = useProposalFormStore(_temp19);
  const setCoverImage = useProposalFormStore(_temp20);
  let t3;
  if ($[3] !== reduce) {
    t3 = reduce === true ? {
      duration: 0
    } : MOTION_ENTRANCE;
    $[3] = reduce;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const motionTransition = t3;
  const motionInitial = reduce === true ? false : FADE_INITIAL;
  const pending = function pending(value_0) {
    return isStreaming && value_0.length === 0;
  };
  let t4;
  if ($[5] !== outcomes) {
    t4 = outcomes.some(_temp21);
    $[5] = outcomes;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const outcomesArrived = t4;
  const outcomesPending = isStreaming && !outcomesArrived;
  let t5;
  if ($[7] !== isStreaming || $[8] !== title) {
    t5 = !isStreaming && detectPredictionStyle(title);
    $[7] = isStreaming;
    $[8] = title;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const showPredictionStyleWarning = t5;
  const {
    minOutcomes,
    maxOutcomes
  } = useOutcomesLimits();
  const limitsReady = minOutcomes !== null && maxOutcomes !== null;
  const canAddMore = limitsReady && outcomes.length < maxOutcomes;
  const canRemove = limitsReady && outcomes.length > minOutcomes;
  const [coverPickerOpen, setCoverPickerOpen] = useState(false);
  const coverNsfw = useNsfwStatusByUrl(coverImageDraft === null ? coverImageUrl : null);
  let t6;
  if ($[10] !== setCoverPickerOpen) {
    t6 = function handleOpenCoverPicker() {
      setCoverPickerOpen(true);
    };
    $[10] = setCoverPickerOpen;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleOpenCoverPicker = t6;
  let t7;
  if ($[12] !== setCoverImageDraft) {
    t7 = function handleCoverImagePending(draft) {
      setCoverImageDraft(draft);
    };
    $[12] = setCoverImageDraft;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const handleCoverImagePending = t7;
  let t8;
  if ($[14] !== setCoverImage) {
    t8 = function handleCoverImageReject(_error) {
      setCoverImage("", false);
    };
    $[14] = setCoverImage;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const handleCoverImageReject = t8;
  let t9;
  if ($[16] !== setCoverImage) {
    t9 = function handleCoverImageRemove(e) {
      e.stopPropagation();
      setCoverImage("", false);
    };
    $[16] = setCoverImage;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const handleCoverImageRemove = t9;
  let t10;
  if ($[18] !== setOutcomeImageDraft) {
    t10 = function handleOutcomeImagePending(index, draft_0) {
      setOutcomeImageDraft(index, draft_0);
    };
    $[18] = setOutcomeImageDraft;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const handleOutcomeImagePending = t10;
  let t11;
  if ($[20] !== setOutcomeImage) {
    t11 = function handleOutcomeImageReject(index_0, _error_0) {
      setOutcomeImage(index_0, "", false);
    };
    $[20] = setOutcomeImage;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const handleOutcomeImageReject = t11;
  let t12;
  if ($[22] !== setOutcomeImage) {
    t12 = function handleOutcomeImageRemove(index_1) {
      setOutcomeImage(index_1, "", false);
    };
    $[22] = setOutcomeImage;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const handleOutcomeImageRemove = t12;
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <AiStreamStatusChip />;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== showPredictionStyleWarning || $[26] !== tForm) {
    t14 = showPredictionStyleWarning && <output className="flex gap-2 rounded-control border border-feedback-warning-border/30 bg-feedback-warning-accent/10 px-3 py-2 text-body text-feedback-warning-content"><Icon icon={Alert01IconSolid} className="mt-0.5 size-4 shrink-0" /><div className="flex flex-col gap-0.5"><p className="font-medium">{tForm("predictionStyleWarningTitle")}</p><p className="text-feedback-warning-content/80">{tForm("predictionStyleWarningBody", {
            exampleA: tForm("predictionStyleWarningExampleA"),
            exampleB: tForm("predictionStyleWarningExampleB")
          })}</p></div></output>;
    $[25] = showPredictionStyleWarning;
    $[26] = tForm;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== coverImagePreview || $[29] !== coverImageUrl || $[30] !== coverNsfw.isNsfw || $[31] !== coverPinFailed || $[32] !== handleCoverImageRemove || $[33] !== handleOpenCoverPicker || $[34] !== isStreaming || $[35] !== motionInitial || $[36] !== motionTransition) {
    t15 = <CoverImageSlot coverImageUrl={coverImageUrl} coverImagePreview={coverImagePreview} pinFailed={coverPinFailed} isStreaming={isStreaming} isFlagged={coverNsfw.isNsfw} onClickOpen={handleOpenCoverPicker} onRemove={handleCoverImageRemove} motionInitial={motionInitial} motionTransition={motionTransition} />;
    $[28] = coverImagePreview;
    $[29] = coverImageUrl;
    $[30] = coverNsfw.isNsfw;
    $[31] = coverPinFailed;
    $[32] = handleCoverImageRemove;
    $[33] = handleOpenCoverPicker;
    $[34] = isStreaming;
    $[35] = motionInitial;
    $[36] = motionTransition;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== tForm) {
    t16 = tForm("marketTickerLabel");
    $[38] = tForm;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== marketTicker || $[41] !== tForm) {
    t17 = tForm("tickerByteCount", {
      count: utf8ByteLength(marketTicker),
      max: PROPOSAL_FIELDS.marketTicker.maxLength
    });
    $[40] = marketTicker;
    $[41] = tForm;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  const t18 = utf8ByteLength(marketTicker) > PROPOSAL_FIELDS.marketTicker.maxLength && "text-feedback-error-content";
  let t19;
  if ($[43] !== t18) {
    t19 = cn("text-right tabular-nums", t18);
    $[43] = t18;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== tForm) {
    t20 = tForm("marketTickerPlaceholder");
    $[45] = tForm;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  const T0 = CategoryLocationFields;
  const t21 = lockedCategory !== undefined;
  const t22 = errText("category");
  const t23 = errText("location");
  let t24;
  if ($[47] !== T0 || $[48] !== category || $[49] !== isStreaming || $[50] !== location || $[51] !== locationEnabled || $[52] !== motionInitial || $[53] !== motionTransition || $[54] !== t21 || $[55] !== t22 || $[56] !== t23 || $[57] !== updateField) {
    t24 = <T0 category={category} categoryLocked={t21} location={location} locationEnabled={locationEnabled} categoryError={t22} locationError={t23} isStreaming={isStreaming} updateField={updateField} motionInitial={motionInitial} motionTransition={motionTransition} />;
    $[47] = T0;
    $[48] = category;
    $[49] = isStreaming;
    $[50] = location;
    $[51] = locationEnabled;
    $[52] = motionInitial;
    $[53] = motionTransition;
    $[54] = t21;
    $[55] = t22;
    $[56] = t23;
    $[57] = updateField;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] !== addOutcome || $[60] !== canAddMore || $[61] !== canRemove || $[62] !== description || $[63] !== errText || $[64] !== handleOutcomeImagePending || $[65] !== handleOutcomeImageReject || $[66] !== handleOutcomeImageRemove || $[67] !== isStreaming || $[68] !== marketTicker || $[69] !== motionInitial || $[70] !== motionTransition || $[71] !== outcomes || $[72] !== outcomesPending || $[73] !== removeOutcome || $[74] !== title || $[75] !== updateOutcomeLabel || $[76] !== updateOutcomeTicker) {
    t25 = <OutcomesSection outcomesPending={outcomesPending} outcomes={outcomes} errText={errText} marketTicker={marketTicker} title={title} description={description} isStreaming={isStreaming} canRemove={canRemove} canAddMore={canAddMore} updateOutcomeLabel={updateOutcomeLabel} updateOutcomeTicker={updateOutcomeTicker} handleOutcomeImagePending={handleOutcomeImagePending} handleOutcomeImageReject={handleOutcomeImageReject} handleOutcomeImageRemove={handleOutcomeImageRemove} removeOutcome={removeOutcome} addOutcome={addOutcome} motionInitial={motionInitial} motionTransition={motionTransition} />;
    $[59] = addOutcome;
    $[60] = canAddMore;
    $[61] = canRemove;
    $[62] = description;
    $[63] = errText;
    $[64] = handleOutcomeImagePending;
    $[65] = handleOutcomeImageReject;
    $[66] = handleOutcomeImageRemove;
    $[67] = isStreaming;
    $[68] = marketTicker;
    $[69] = motionInitial;
    $[70] = motionTransition;
    $[71] = outcomes;
    $[72] = outcomesPending;
    $[73] = removeOutcome;
    $[74] = title;
    $[75] = updateOutcomeLabel;
    $[76] = updateOutcomeTicker;
    $[77] = t25;
  } else {
    t25 = $[77];
  }
  let t26;
  if ($[78] !== handleCoverImagePending || $[79] !== setCoverPickerOpen) {
    t26 = draft_1 => {
      setCoverPickerOpen(false);
      handleCoverImagePending(draft_1);
    };
    $[78] = handleCoverImagePending;
    $[79] = setCoverPickerOpen;
    $[80] = t26;
  } else {
    t26 = $[80];
  }
  let t27;
  if ($[81] !== coverPickerOpen || $[82] !== handleCoverImageReject || $[83] !== setCoverPickerOpen || $[84] !== t26 || $[85] !== title) {
    t27 = <MarketImagePickerModal open={coverPickerOpen} onOpenChange={setCoverPickerOpen} mode="cover" marketTitle={title} onPending={t26} onReject={handleCoverImageReject} />;
    $[81] = coverPickerOpen;
    $[82] = handleCoverImageReject;
    $[83] = setCoverPickerOpen;
    $[84] = t26;
    $[85] = title;
    $[86] = t27;
  } else {
    t27 = $[86];
  }
  return <LazyMotion features={domAnimation}><div className="flex flex-col gap-4">{t13}{t14}<div className="flex items-stretch gap-3">{t15}<div className="flex min-w-0 flex-1 flex-col gap-3">{pending(title) ? <SkeletonField label={t("fields.title")} className="h-9" /> : <m.div key="title" initial={motionInitial} animate={FADE_ANIMATE} transition={motionTransition}><InputField label={t("fields.title")} value={title} onChange={e_0 => updateField("title", e_0.target.value)} maxLength={PROPOSAL_FIELDS.title.maxLength} error={errText("title")} /></m.div>}<InputField id="ai-market-ticker" label={t16} value={marketTicker} {...tickerInput} description={t17} descriptionClassName={t19} placeholder={t20} prefix="$" error={errText("marketTicker")} /></div></div>{pending(description) ? <SkeletonField label={t("fields.description")} className="h-20" /> : <m.div key="description" initial={motionInitial} animate={FADE_ANIMATE} transition={motionTransition}><TextareaField label={t("fields.description")} value={description} onChange={e_1 => updateField("description", e_1.target.value)} rows={3} error={errText("description")} /></m.div>}{t24}{t25}{t27}</div></LazyMotion>;
}

// ---------------------------------------------------------------------------
// Cover image slot
// ---------------------------------------------------------------------------
function _temp21(o) {
  return o.label.trim().length > 0;
}
function _temp20(s_18) {
  return s_18.setCoverImage;
}
function _temp19(s_17) {
  return s_17.setCoverImageDraft;
}
function _temp18(s_16) {
  return s_16.setOutcomeImage;
}
function _temp17(s_15) {
  return s_15.setOutcomeImageDraft;
}
function _temp16(s_14) {
  return s_14.removeOutcome;
}
function _temp15(s_13) {
  return s_13.addOutcome;
}
function _temp14(s_12) {
  return s_12.updateOutcomeTicker;
}
function _temp13(s_11) {
  return s_11.updateOutcomeLabel;
}
function _temp12(s_10) {
  return s_10.updateField;
}
function _temp11(s_9) {
  return s_9.outcomes;
}
function _temp10(s_8) {
  return s_8.coverImageUploading;
}
function _temp9(s_7) {
  return s_7.coverImageDraft;
}
function _temp8(s_6) {
  return s_6.coverImagePreview;
}
function _temp7(s_5) {
  return s_5.coverImageUrl;
}
function _temp6(s_4) {
  return s_4.marketTicker;
}
function _temp5(s_3) {
  return s_3.location;
}
function _temp4(s_2) {
  return s_2.category;
}
function _temp3(s_1) {
  return s_1.description;
}
function _temp2(s_0) {
  return s_0.title;
}
function _temp(s) {
  return s.aiStreamStatus;
}
interface CoverImageSlotProps {
  /** Durable IPFS URL (submission source of truth). Empty until `cover.pinned`. */
  coverImageUrl: string;
  /** Inline preview data-URI (display source of truth during AI review). */
  coverImagePreview?: string | undefined;
  /** True once the pin permanently failed — preview still shows, but the slot
   *  has no durable URL so submission is blocked; we flag it for the user. */
  pinFailed: boolean;
  isStreaming: boolean;
  isFlagged: boolean;
  onClickOpen: () => void;
  onRemove: (e: React.MouseEvent<HTMLButtonElement>) => void;
  motionInitial: false | {
    opacity: number;
    y: number;
  };
  motionTransition: {
    duration: number;
  } | typeof MOTION_ENTRANCE;
}

/**
 * Cover-image specialisation of the shared `StreamedImageSlot`. Three
 * states (populated / streaming / empty) are owned by the lifted
 * component; this wrapper just threads the localised alt + remove
 * labels through (#34 audit).
 */
function CoverImageSlot(t0) {
  const $ = _c(21);
  const {
    coverImageUrl,
    coverImagePreview,
    pinFailed,
    isStreaming,
    isFlagged,
    onClickOpen,
    onRemove,
    motionInitial,
    motionTransition
  } = t0;
  const t = useTranslations("aiProposal.editForm");
  const tForm = useTranslations("launchpad.form");
  let t1;
  if ($[0] !== t) {
    t1 = t("fields.coverImage");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== tForm) {
    t2 = tForm("coverImageRemove");
    $[2] = tForm;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== tForm) {
    t3 = tForm("coverImageFailedTitle");
    $[4] = tForm;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== coverImagePreview) {
    t4 = coverImagePreview !== undefined ? {
      imagePreview: coverImagePreview
    } : {};
    $[6] = coverImagePreview;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== coverImageUrl || $[9] !== isFlagged || $[10] !== isStreaming || $[11] !== motionInitial || $[12] !== motionTransition || $[13] !== onClickOpen || $[14] !== onRemove || $[15] !== pinFailed || $[16] !== t1 || $[17] !== t2 || $[18] !== t3 || $[19] !== t4) {
    t5 = <StreamedImageSlot imageUrl={coverImageUrl} isStreaming={isStreaming} isFlagged={isFlagged} onClickOpen={onClickOpen} onRemove={onRemove} motionInitial={motionInitial} motionTransition={motionTransition} altLabel={t1} removeLabel={t2} pinFailed={pinFailed} pinFailedLabel={t3} {...t4} />;
    $[8] = coverImageUrl;
    $[9] = isFlagged;
    $[10] = isStreaming;
    $[11] = motionInitial;
    $[12] = motionTransition;
    $[13] = onClickOpen;
    $[14] = onRemove;
    $[15] = pinFailed;
    $[16] = t1;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  return t5;
}

// ---------------------------------------------------------------------------
// Category + Location fields
// ---------------------------------------------------------------------------

type UpdateField = ReturnType<typeof useProposalFormStore.getState>['updateField'];
interface CategoryLocationFieldsProps {
  category: string;
  categoryLocked: boolean;
  location: string;
  locationEnabled: boolean;
  categoryError?: string | undefined;
  locationError?: string | undefined;
  isStreaming: boolean;
  updateField: UpdateField;
  motionInitial: MotionInitial;
  motionTransition: MotionTransition;
}

/**
 * Category + Location 2-col grid, mirroring the manual flow's step1. While
 * streaming AND the slice is still empty we show a `<SkeletonField>`; the
 * moment a value lands the `<SelectField>` renders. State stays in
 * `ProposalDetailsForm` — this is a pure render of the threaded values/setter.
 */
function CategoryLocationFields(t0) {
  const $ = _c(28);
  const {
    category,
    categoryLocked,
    location,
    locationEnabled,
    categoryError,
    locationError,
    isStreaming,
    updateField,
    motionInitial,
    motionTransition
  } = t0;
  const t = useTranslations("aiProposal.editForm");
  const tForm = useTranslations("launchpad.form");
  let t1;
  if ($[0] !== isStreaming) {
    t1 = function pending(value) {
      return isStreaming && value.length === 0;
    };
    $[0] = isStreaming;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const pending = t1;
  if (categoryLocked && !locationEnabled) {
    return null;
  }
  const t2 = locationEnabled && !categoryLocked && "sm:grid-cols-2";
  let t3;
  if ($[2] !== t2) {
    t3 = cn("grid grid-cols-1 gap-3", t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== category || $[5] !== categoryError || $[6] !== categoryLocked || $[7] !== motionInitial || $[8] !== motionTransition || $[9] !== pending || $[10] !== t || $[11] !== tForm || $[12] !== updateField) {
    t4 = !categoryLocked && (pending(category) ? <SkeletonField label={t("fields.category")} className="h-9" /> : <m.div key="category" initial={motionInitial} animate={FADE_ANIMATE} transition={motionTransition}><SelectField label={tForm("categoryLabel")} items={CATEGORY_OPTIONS} value={category.length > 0 ? category : undefined} onValueChange={v => updateField("category", v)} placeholder={tForm("categoryPlaceholder")} error={categoryError} /></m.div>);
    $[4] = category;
    $[5] = categoryError;
    $[6] = categoryLocked;
    $[7] = motionInitial;
    $[8] = motionTransition;
    $[9] = pending;
    $[10] = t;
    $[11] = tForm;
    $[12] = updateField;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== location || $[15] !== locationEnabled || $[16] !== locationError || $[17] !== motionInitial || $[18] !== motionTransition || $[19] !== pending || $[20] !== t || $[21] !== tForm || $[22] !== updateField) {
    t5 = locationEnabled ? pending(location) ? <SkeletonField label={t("fields.location")} className="h-9" /> : <m.div key="location" initial={motionInitial} animate={FADE_ANIMATE} transition={motionTransition}><SelectField label={tForm("locationLabel")} items={LOCATION_OPTIONS} value={location.length > 0 ? location : undefined} onValueChange={v_0 => updateField("location", v_0)} placeholder={tForm("locationPlaceholder")} error={locationError} /></m.div> : null;
    $[14] = location;
    $[15] = locationEnabled;
    $[16] = locationError;
    $[17] = motionInitial;
    $[18] = motionTransition;
    $[19] = pending;
    $[20] = t;
    $[21] = tForm;
    $[22] = updateField;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== t3 || $[25] !== t4 || $[26] !== t5) {
    t6 = <div className={t3}>{t4}{t5}</div>;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  return t6;
}

// ---------------------------------------------------------------------------
// Outcomes section
// ---------------------------------------------------------------------------

interface OutcomesSectionProps {
  outcomesPending: boolean;
  outcomes: AiOutcome[];
  /** Translate a step-2 validation error for a given field key, gated on
   *  `stepsAttempted`. Returns undefined when valid / not yet attempted. */
  errText: (field: string) => string | undefined;
  marketTicker: string;
  title: string;
  description: string;
  isStreaming: boolean;
  canRemove: boolean;
  canAddMore: boolean;
  updateOutcomeLabel: (index: number, label: string) => void;
  updateOutcomeTicker: (index: number, ticker: string) => void;
  handleOutcomeImagePending: (index: number, draft: MarketImageDraft) => void;
  handleOutcomeImageReject: (index: number, error: Error) => void;
  handleOutcomeImageRemove: (index: number) => void;
  removeOutcome: (index: number) => void;
  addOutcome: () => void;
  motionInitial: MotionInitial;
  motionTransition: MotionTransition;
}

/**
 * Outcomes block — reuses the manual flow's `<OutcomeInput>` so the row
 * shape, ticker prefix, image picker, and remove affordance are
 * byte-identical between flows. All state stays owned by `ProposalDetailsForm`;
 * this component is a pure render of the values/setters threaded in.
 */
function OutcomesSection(t0) {
  const $ = _c(36);
  const {
    outcomesPending,
    outcomes,
    errText,
    marketTicker,
    title,
    description,
    isStreaming,
    canRemove,
    canAddMore,
    updateOutcomeLabel,
    updateOutcomeTicker,
    handleOutcomeImagePending,
    handleOutcomeImageReject,
    handleOutcomeImageRemove,
    removeOutcome,
    addOutcome,
    motionInitial,
    motionTransition
  } = t0;
  const t = useTranslations("aiProposal.editForm");
  const tForm = useTranslations("launchpad.form");
  const showSimilarMarkets = useProposalFormStore(_temp22);
  let t1;
  if ($[0] !== t) {
    t1 = t("fields.outcomes");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className="flex items-center justify-between"><Label>{t1}</Label></div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== canRemove || $[5] !== errText || $[6] !== handleOutcomeImagePending || $[7] !== handleOutcomeImageReject || $[8] !== handleOutcomeImageRemove || $[9] !== isStreaming || $[10] !== marketTicker || $[11] !== motionInitial || $[12] !== motionTransition || $[13] !== outcomes || $[14] !== outcomesPending || $[15] !== removeOutcome || $[16] !== title || $[17] !== updateOutcomeLabel || $[18] !== updateOutcomeTicker) {
    t3 = outcomesPending ? <div className="flex flex-col gap-2"><Skeleton className="h-24 w-full rounded-control" /><Skeleton className="h-24 w-full rounded-control" /></div> : <div className="flex flex-col gap-3">{outcomes.map((outcome, index) => <m.div key={outcome.id} initial={motionInitial} animate={FADE_ANIMATE} transition={motionTransition}><OutcomeInput index={index} label={outcome.label} ticker={outcome.ticker} error={errText(`outcomeLabel_${String(index)}`) ?? errText(`outcomeDuplicate_${String(index)}`) ?? null} tickerError={errText(`outcomeTicker_${String(index)}`) ?? errText(`outcomeTickerDuplicate_${String(index)}`) ?? null} marketTicker={marketTicker} marketTitle={title} imageUrl={outcome.imageUrl !== "" ? outcome.imageUrl : null} onLabelChange={label => updateOutcomeLabel(index, label)} onTickerChange={ticker => updateOutcomeTicker(index, ticker)} onImagePending={draft => handleOutcomeImagePending(index, draft)} onImageReject={err => handleOutcomeImageReject(index, err)} onImageRemove={() => handleOutcomeImageRemove(index)} onRemove={() => removeOutcome(index)} canRemove={canRemove} imageLoading={isStreaming && outcome.imageUrl === "" && (outcome.imagePreview === undefined || outcome.imagePreview.length === 0)} eagerImage={true} pinFailed={outcome.imagePreview !== undefined && outcome.imagePreview.length > 0 && outcome.imageDraft === null && outcome.imageUrl === "" && !outcome.imageUploading} {...outcome.imagePreview !== undefined ? {
          imagePreview: outcome.imagePreview
        } : {}} prefetchImageSearch={false} /></m.div>)}</div>;
    $[4] = canRemove;
    $[5] = errText;
    $[6] = handleOutcomeImagePending;
    $[7] = handleOutcomeImageReject;
    $[8] = handleOutcomeImageRemove;
    $[9] = isStreaming;
    $[10] = marketTicker;
    $[11] = motionInitial;
    $[12] = motionTransition;
    $[13] = outcomes;
    $[14] = outcomesPending;
    $[15] = removeOutcome;
    $[16] = title;
    $[17] = updateOutcomeLabel;
    $[18] = updateOutcomeTicker;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  const t4 = !canAddMore || outcomesPending;
  let t5;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Icon icon={PlusSignIcon} className="size-4" />;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== tForm) {
    t6 = tForm("addOutcome");
    $[21] = tForm;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== addOutcome || $[24] !== t4 || $[25] !== t6) {
    t7 = <AnimatedButton type="button" variant="secondary" onClick={addOutcome} disabled={t4} className="w-full">{t5}{t6}</AnimatedButton>;
    $[23] = addOutcome;
    $[24] = t4;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== description || $[28] !== showSimilarMarkets || $[29] !== title) {
    t8 = showSimilarMarkets ? <SimilarMarketsNudge queryText={`${title}\n${description}`} /> : null;
    $[27] = description;
    $[28] = showSimilarMarkets;
    $[29] = title;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== t2 || $[32] !== t3 || $[33] !== t7 || $[34] !== t8) {
    t9 = <div className="flex flex-col gap-3">{t2}{t3}{t7}{t8}</div>;
    $[31] = t2;
    $[32] = t3;
    $[33] = t7;
    $[34] = t8;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  return t9;
}

// ---------------------------------------------------------------------------
// Skeleton field
// ---------------------------------------------------------------------------

// SkeletonField lifted to `@/components/launchpad/ai/skeleton-field`
// so the Suggested-outcomes wizard shares the same streamed-field
// skeleton (#34 audit).
function _temp22(state) {
  return state.aiMarketKind !== "prediction";
}